import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";

interface ProAvatarProps {
  avatarUrl?: string | null;
  email?: string | null;
  isPro?: boolean;
  isFounder?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

const getInitials = (email: string | null | undefined): string => {
  if (!email) return "?";
  const name = email.split("@")[0];
  return name.substring(0, 2).toUpperCase();
};

export const ProAvatar = ({
  avatarUrl,
  email,
  isPro = false,
  isFounder = false,
  size = "md",
  className,
}: ProAvatarProps) => {
  const showBadge = isPro || isFounder;
  const ringColor = isFounder
    ? "ring-4 ring-gradient-to-r from-amber-500 to-orange-500"
    : "ring-4 ring-cyan-500";

  return (
    <div className={cn("relative inline-block", className)}>
      {/* Avatar with conditional ring */}
      <Avatar className={cn(sizeClasses[size], showBadge && ringColor)}>
        {avatarUrl ? (
          <AvatarImage src={avatarUrl} alt={email || "User avatar"} />
        ) : (
          <AvatarFallback className="bg-muted text-muted-foreground font-semibold">
            {email ? getInitials(email) : <User className="h-4 w-4" />}
          </AvatarFallback>
        )}
      </Avatar>

      {/* Badge at bottom center */}
      {showBadge && (
        <div className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 z-10">
          <Badge
            className={cn(
              "text-white text-[8px] px-1 py-0 font-semibold",
              isFounder
                ? "bg-gradient-to-r from-amber-500 to-orange-500"
                : "bg-cyan-500"
            )}
          >
            {isFounder ? "FOUNDER" : "PRO"}
          </Badge>
        </div>
      )}
    </div>
  );
};
